import { useState, useEffect, useCallback, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import {
  startGetBrandInformation,
  startSaveBrandInformationFromUrl,
  startExtractBrandInformationFromUrl,
  startDetectAndSaveBrandLanguage,
  startAnalyzeBusinessProductImages,
} from "../../../../actions/adsCampaigns";
import {
  startGetBrandSetupVersions,
  startSaveBrandSetupVersion,
  startGetBrandSetupVersionSnapshot,
} from "../../../../actions/brandSetupVersions";
import {
  startGetBusinessName,
  startGetAdsUsers,
} from "../../../../actions/user";
import {
  ConfirmAlert,
  SimpleAlert,
} from "../../../../components/Alerts/Alerts";
import {
  ALERT_ICON_TYPE_SUCCESS,
  ALERT_ICON_TYPE_WARNING,
} from "../../../../utils/constants";
import {
  READY,
  MODAL_BRAND_SETUP_TITLE,
  MODAL_BRAND_SETUP_DESCRIPTION,
  MODAL_BRAND_SETUP_CONFIRM_TITLE,
  MODAL_BRAND_SETUP_CONFIRM_DESCRIPTION,
  MODAL_BRAND_SETUP_CONFIRM_BUTTON,
  MODAL_BRAND_SETUP_REVIEW_BUTTON,
  FIELD_REQUIRED,
  WARNING,
  BRAND_SETUP_EXTRACTION_WARNING_MESSAGE,
  ERROR_OCCURRED,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { LaunchError } from "../../../../utils/errors";
import { getValidationRules } from "../utils/validationRules";
import {
  getDefaultFormValues,
  someImportantDataExtractedIsEmpty,
} from "../utils/formDataTransformers";
import {
  normalizeApiDataToForm,
  normalizeFormDataToApi,
  mergeBusinessProductImagesOnSave,
  needsProductImageMetadata,
} from "../utils/formDataTransformers";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import UsersTable from "../../../../pages/Users/components/UsersTable";
import { validateBrandSetupTextFields } from "../utils/blockedBrandsValidation";

export const useBrandSetup = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isReExtracting, setIsReExtracting] = useState(false);
  const [isExtracted, setIsExtracted] = useState(false);
  const [firstExtracting, setFirstExtracting] = useState(false);
  const [usedManualOption, setUsedManualOption] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [users, setUsers] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [versions, setVersions] = useState([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [selectedVersionID, setSelectedVersionID] = useState(null);
  const [
    hasAdvancedConfigurationAdditionalAssets,
    setHasAdvancedConfigurationAdditionalAssets,
  ] = useState(false);
  const [hasAdditionalAssets, setHasAdditionalAssets] = useState(false);
  const dbProductImagesRef = useRef([]);
  const lastSavedDataRef = useRef(null);

  const { user } = useSelectorApp((state) => state.auth);
  const isAnonymous = user?.isAnonymous;
  const isPlanActive = useSelectorApp((state) => state.payments.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo?.isTrial
  );

  const adsCampaignsIDsByAgency = useSelectorApp(
    (state) => state.adsCampaigns.adsCampaignsIDsByAgency
  );
  const totalCampaignsCreated = adsCampaignsIDsByAgency?.length || 0;

  const formMethods = useForm({
    defaultValues: getDefaultFormValues(),
    mode: "onChange",
  });

  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState,
    handleSubmit,
    trigger,
    setError,
  } = formMethods;

  const {
    append: appendBusinessProductImages,
    remove: removeBusinessProductImage,
  } = useFieldArray({
    control,
    name: "businessProductImages",
  });

  const {
    fields: additionalAssetsFields,
    append: appendAdditionalAsset,
    remove: removeAdditionalAsset,
    update: updateAdditionalAsset,
  } = useFieldArray({
    control,
    name: "additionalAssets",
  });

  const {
    fields: businessCompetitorsFields,
    append: appendBusinessCompetitors,
    remove: removeBusinessCompetitors,
  } = useFieldArray({
    control,
    name: "businessCompetitors",
  });

  const {
    fields: socialNetworksFields,
    append: appendSocialNetworks,
    remove: removeSocialNetworks,
  } = useFieldArray({
    control,
    name: "socialNetworks",
  });

  useFocusErrorForm(formState);
  const validationRules = getValidationRules(t);

  const loadInitialData = useCallback(async () => {
    reset();
    setIsLoading(true);
    setIsLoadingVersions(true);
    setSelectedVersionID(null);
    try {
      const [response, versionsResponse] = await Promise.all([
        dispatch(startGetBrandInformation()),
        dispatch(startGetBrandSetupVersions()),
      ]);

      if (response.ok) {
        const { data } = response;
        setHasAdvancedConfigurationAdditionalAssets(
          data?.hasAdvancedConfigurationAdditionalAssets === true
        );
        setHasAdditionalAssets(data?.hasAdditionalAssets === true);
        const normalizedData = normalizeApiDataToForm(data);
        reset(normalizedData);
        dbProductImagesRef.current = normalizedData.businessProductImages || [];
        lastSavedDataRef.current = normalizeFormDataToApi(normalizedData);
        if (
          data?.businessDescription ||
          data?.businessTextStyle ||
          data?.businessCompetitors ||
          data?.socialNetworks ||
          data?.websiteUrl
        ) {
          setIsExtracted(true);
          setFirstExtracting(true);
        }
      }

      if (versionsResponse.ok) {
        setVersions(versionsResponse.data || []);
      }
    } catch (error) {
      console.error("Error loading brand information:", error);
    } finally {
      setIsLoadingVersions(false);
    }
    setIsLoadingUsers(true);
    try {
      const responseUsers = await dispatch(
        startGetAdsUsers({
          onCallbackLoadingFinish: () => setIsLoading(false),
        })
      );
      setUsers(responseUsers || {});
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setIsLoadingUsers(false);
      setIsLoading(false);
    }
  }, [dispatch, reset]);

  const handleReExtract = useCallback(
    async (url) => {
      if (!url) return;
      if (isExtracted && !usedManualOption && !isAnonymous) {
        const confirm = await ConfirmAlert({
          title: t(MODAL_BRAND_SETUP_TITLE),
          html: (
            <Box
              sx={{
                maxHeight: "400px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="body1">
                {t(MODAL_BRAND_SETUP_DESCRIPTION)}
              </Typography>
              <UsersTable
                data={users || {}}
                loading={isLoadingUsers}
                showEmail={false}
                showActions={false}
                showRole={true}
                filterByAdvertiserAssigned={userID}
              />
            </Box>
          ),
        });
        if (!confirm.isConfirmed) {
          return;
        }
      }
      const currentUrl = url || getValues("websiteUrl");
      if (!currentUrl) {
        setError("websiteUrl", {
          message: t(FIELD_REQUIRED),
        });
        return;
      }
      const isValid = await trigger("websiteUrl");
      if (!isValid) {
        return;
      }
      setIsReExtracting(true);
      setIsExtracting(true);
      try {
        const response = await dispatch(
          startExtractBrandInformationFromUrl(currentUrl)
        );
        if (response.ok) {
          const normalized = normalizeApiDataToForm(response.data);
          const isImportantDataEmpty =
            someImportantDataExtractedIsEmpty(normalized);
          if (isImportantDataEmpty) {
            SimpleAlert({
              title: t(WARNING),
              text: t(BRAND_SETUP_EXTRACTION_WARNING_MESSAGE),
              icon: ALERT_ICON_TYPE_WARNING,
            });
          }
          reset(normalized);
          setIsExtracted(true);
        } else {
          const defaultValues = getDefaultFormValues();
          reset({
            ...defaultValues,
            websiteUrl: currentUrl,
          });
        }
      } catch (error) {
        console.error("Error re-extracting brand information:", error);
      } finally {
        setIsReExtracting(false);
        setIsExtracting(false);
      }
    },
    [
      dispatch,
      getValues,
      reset,
      trigger,
      setError,
      isExtracted,
      usedManualOption,
      users,
      isLoadingUsers,
      userID,
      t,
      isAnonymous,
    ]
  );

  const getBusinessNameLowercase = (businessName) =>
    (businessName || "").toLowerCase().trim();

  const saveFormData = useCallback(
    async (formData) => {
      const confirm = await ConfirmAlert({
        title: t(MODAL_BRAND_SETUP_CONFIRM_TITLE),
        text: t(MODAL_BRAND_SETUP_CONFIRM_DESCRIPTION),
        confirmButtonText: t(MODAL_BRAND_SETUP_CONFIRM_BUTTON),
        cancelButtonText: t(MODAL_BRAND_SETUP_REVIEW_BUTTON),
      });
      if (!confirm.isConfirmed) {
        return { success: false, cancelled: true };
      }
      setIsSaving(true);
      let submitData = {
        ...formData,
        businessProductImages:
          getValues("businessProductImages") ||
          formData.businessProductImages ||
          [],
      };
      if (!isExtracted && !usedManualOption && !formData.websiteUrl) {
        setError("websiteUrl", {
          message: t(FIELD_REQUIRED),
        });
        setIsSaving(false);
        return { success: false, error: "URL required" };
      }
      if (!isExtracted && !usedManualOption && formData.websiteUrl) {
        const isValid = await trigger("websiteUrl");
        if (!isValid) {
          setIsSaving(false);
          return { success: false, error: "Invalid URL" };
        }
        setIsExtracting(true);
        const extractResponse = await dispatch(
          startExtractBrandInformationFromUrl(formData.websiteUrl)
        );
        if (extractResponse.ok) {
          const {
            businessName = "",
            businessDescription = "",
            businessTextStyle = "",
            businessLookAndFeel = "",
            businessValueProposition = "",
            businessOffering = "",
            businessIdealCustomerProfile = "",
            businessCountry = "",
            businessColors = [],
            businessCompetitors = [],
            logoUrl = "",
            socialNetworks = [],
          } = extractResponse.data;
          const businessBuyerPerson =
            extractResponse.data?.businessBuyerPerson ??
            extractResponse.data?.businessBuyerPerson ??
            [];
          const extractedData = {
            businessName,
            businessDescription,
            businessTextStyle,
            businessLookAndFeel,
            businessValueProposition,
            businessOffering,
            businessIdealCustomerProfile,
            businessBuyerPerson,
            businessCountry,
            businessColors,
            businessCompetitors:
              businessCompetitors.length > 0 ? businessCompetitors : [],
            logoUrl,
            socialNetworks: socialNetworks.length > 0 ? socialNetworks : [],
          };
          const payload = { ...getValues(), ...extractedData };
          reset(payload);
          submitData = payload;
        }
        setIsExtracting(false);
        setIsExtracted(true);
      }
      submitData.businessNameLowercase = getBusinessNameLowercase(
        submitData.businessName
      );
      if (!submitData.businessColors) {
        submitData.businessColors = getValues("businessColors") || [];
      }
      submitData.businessProductImages =
        getValues("businessProductImages") ||
        submitData.businessProductImages ||
        [];
      submitData.businessProductImages = mergeBusinessProductImagesOnSave({
        formItems: submitData.businessProductImages || [],
        dbItems: dbProductImagesRef.current,
      });
      try {
        validateBrandSetupTextFields({
          formData: submitData,
          isPlanActive,
          isTrial,
        });
        const apiData = normalizeFormDataToApi(submitData);
        const shouldAnalyzeProductImages = needsProductImageMetadata(
          apiData.businessProductImages
        );
        const response = await dispatch(
          startSaveBrandInformationFromUrl(apiData)
        );
        if (response.ok) {
          dbProductImagesRef.current = apiData.businessProductImages || [];
          const source = selectedVersionID ? "restore" : "manual_save";
          const versionResult = await dispatch(
            startSaveBrandSetupVersion({
              apiData,
              previousApiData: lastSavedDataRef.current,
              source,
            })
          );

          if (versionResult?.ok && !versionResult?.skipped) {
            lastSavedDataRef.current = apiData;
            const versionsResponse = await dispatch(
              startGetBrandSetupVersions()
            );
            if (versionsResponse.ok) {
              setVersions(versionsResponse.data || []);
            }
            setSelectedVersionID(null);
          } else if (versionResult?.ok && versionResult?.skipped) {
            lastSavedDataRef.current = apiData;
          }

          dispatch(startDetectAndSaveBrandLanguage());
          if (shouldAnalyzeProductImages) {
            void dispatch(startAnalyzeBusinessProductImages());
          }
          await dispatch(startGetBusinessName());
          if (!firstExtracting && totalCampaignsCreated === 0) {
            if (isAnonymous) {
              return { success: true, showCreateAccountModal: true };
            } else {
              return { success: true, showSuccessModal: true };
            }
          } else {
            SimpleAlert({
              title: t(READY),
              icon: ALERT_ICON_TYPE_SUCCESS,
            });
            return { success: true };
          }
        }
        return { success: false, error: response.error };
      } catch (error) {
        const processedError = await dispatch(
          LaunchError(
            {
              code: ERROR_OCCURRED,
              message: t(ERROR_OCCURRED),
            },
            true,
            [],
            {
              functionName: "saveFormData",
              skipSentry: true,
            }
          )
        );
        return { success: false, error: processedError || error };
      } finally {
        setIsSaving(false);
      }
    },
    [
      dispatch,
      t,
      isExtracted,
      usedManualOption,
      trigger,
      setError,
      getValues,
      reset,
      firstExtracting,
      totalCampaignsCreated,
      isAnonymous,
      isPlanActive,
      isTrial,
      selectedVersionID,
    ]
  );

  const onSelectVersion = useCallback(
    async ({ versionID }) => {
      if (!versionID) {
        const response = await dispatch(startGetBrandInformation());
        if (response.ok) {
          const normalizedData = normalizeApiDataToForm(response.data);
          reset(normalizedData);
          dbProductImagesRef.current =
            normalizedData.businessProductImages || [];
        }
        setSelectedVersionID(null);
        return;
      }

      const snapshotResponse = await dispatch(
        startGetBrandSetupVersionSnapshot({ versionID })
      );

      if (snapshotResponse.ok) {
        const normalizedData = normalizeApiDataToForm(snapshotResponse.data);
        reset(normalizedData);
        dbProductImagesRef.current = normalizedData.businessProductImages || [];
        setSelectedVersionID(versionID);
      }
    },
    [dispatch, reset]
  );

  const handleNotHaveWebsite = useCallback(() => {
    setError("websiteUrl", {});
    setValue("websiteUrl", "");
    setValue("businessName", "");
    setValue("businessDescription", "");
    setValue("businessTextStyle", "");
    setValue("businessLookAndFeel", "");
    setValue("businessValueProposition", "");
    setValue("businessOffering", "");
    setValue("businessIdealCustomerProfile", "");
    setValue("businessBuyerPerson", []);
    setValue("businessColors", []);
    setValue("businessCompetitors", []);
    setValue("logoUrl", "");
    setValue("businessProductImages", []);
    setValue("additionalAssets", []);
    setValue("businessCountry", "");
    setValue("socialNetworks", []);
    dbProductImagesRef.current = [];
    setIsExtracted(true);
    setIsExtracting(false);
    setUsedManualOption(true);
    trigger("websiteUrl");
  }, [setError, setValue, trigger]);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  return {
    control,
    errors: formState.errors,
    watch,
    setValue,
    getValues,
    reset,
    formState,
    handleSubmit,
    trigger,
    setError,
    validationRules,
    businessCompetitorsFields,
    appendBusinessCompetitors,
    removeBusinessCompetitors,
    socialNetworksFields,
    appendSocialNetworks,
    removeSocialNetworks,
    isLoading,
    isSaving,
    isReExtracting,
    isExtracted,
    firstExtracting,
    usedManualOption,
    isExtracting,
    users,
    isLoadingUsers,
    handleReExtract,
    saveFormData,
    handleNotHaveWebsite,
    appendBusinessProductImages,
    removeBusinessProductImage,
    additionalAssetsFields,
    appendAdditionalAsset,
    removeAdditionalAsset,
    updateAdditionalAsset,
    versions,
    isLoadingVersions,
    selectedVersionID,
    onSelectVersion,
    hasAdvancedConfigurationAdditionalAssets,
    hasAdditionalAssets,
  };
};
